import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, map } from 'rxjs';

import { AppsSiteManagement, Site, SiteRenewal } from '../../models';
import { SNACKBAR_CONSTANTS } from '../../shared/constants';
import { ClientErrorStatusCodes, ErrorMessages, LicenseTiers } from '../../shared/enums';
import { SitesDataService } from '../data-services';
import { DateConversionService } from '../utility-services';
import { SiteManagementLogicServiceModel } from './models';

@Injectable({
    providedIn: 'root',
})

export class SiteManagementLogicService {
    model: SiteManagementLogicServiceModel = new SiteManagementLogicServiceModel();
    private _isLoading = new BehaviorSubject<boolean>(false);
    private _isLoaded = new BehaviorSubject<boolean>(false);

    isLoading$ = this._isLoading.asObservable();
    isLoaded$ = this._isLoaded.asObservable();

    constructor(
        private _sitesDataService: SitesDataService,
        private _dateConversionService: DateConversionService,
        private _snackBar: MatSnackBar
    ) { }

    clearModel() {
        this.model = new SiteManagementLogicServiceModel();
        this._isLoading.next(false);
        this._isLoaded.next(false);
    }

    getSiteRenewalSummaryAllApps(): void {
        if (this._isLoading.value || this._isLoaded.value) {
            return;
        }
        this._isLoading.next(true);
        this._sitesDataService.getSiteRenewalSummaryAllApps()
            .pipe(map((appsSiteManagement: AppsSiteManagement) => {
                appsSiteManagement.siteRenewals.forEach(siteRenewal => {
                    siteRenewal.dateCreated = this._dateConversionService.safeParseDate(siteRenewal.dateCreated);
                    siteRenewal.lastActivityDate = this._dateConversionService.safeParseDate(siteRenewal.lastActivityDate);
                    siteRenewal.subscriptionStartDate = this._dateConversionService.safeParseDate(siteRenewal.subscriptionStartDate);
                    siteRenewal.subscriptionEndDate = this._dateConversionService.safeParseDate(siteRenewal.subscriptionEndDate);
                });
                return appsSiteManagement;
            })).subscribe({
                next: (appsSiteManagement: AppsSiteManagement) => {
                    this.model.appsSiteManagement = appsSiteManagement;
                    this.model.appNames = [...new Set(appsSiteManagement.siteRenewals.map(sa => sa.appName))].sort();
                    this.model.licenseTiers = [...new Set(appsSiteManagement.siteRenewals.map(sa => sa.licenseTier))].sort();
                    this._isLoading.next(false);
                    this._isLoaded.next(true);
                },
                error: () => {
                    this._isLoading.next(false);
                    this._isLoaded.next(false);
                }
            });
    }

    updateSiteActivation(siteToActivate: SiteRenewal, newActiveStatus: boolean) {
        siteToActivate.isLoading = true;
        this._sitesDataService.updateSiteActive(siteToActivate.appId!, { id: siteToActivate.id, active: newActiveStatus } as Site, newActiveStatus).subscribe({
            next: (site: Site) => {
                if (this.isRenewalDateYearFromNow(site) && site.active) {
                    this._snackBar.open(`Site expiration date has been set and ${site.siteName}' is now active`, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
                else {
                    this._snackBar.open(
                        `The site '${site.siteName}' is now '${site.active ? 'active' : 'de-activated'}' `,
                        SNACKBAR_CONSTANTS.CLOSE,
                        { duration: 3000 }
                    );
                }
                if (!this.isSiteAppPerSite(siteToActivate)) {
                    this.getActiveSitesCountByAppId(siteToActivate);
                }
                this.updateSiteRenewalInformation(siteToActivate.appId!, site);
                siteToActivate.isLoading = false;
            },
            error: (error) => {
                if (error?.status === ClientErrorStatusCodes.Conflict) {
                    switch (error.error) {
                        case ErrorMessages.pastSiteMaxLimit:
                            this._snackBar.open(`Action disabled. You are over your active site limit.`, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                            break;
                        case ErrorMessages.invalidChargeNumber:
                            this._snackBar.open(ErrorMessages.invalidChargeNumber, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                            break;
                        default:
                            this._snackBar.open('There was an error updating the record', SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    }
                }
                siteToActivate.isLoading = false;
            },
            complete: () => { },
        });
    }

    updateSiteAutoRenew(siteToActivate: SiteRenewal, newAutoRenewStatus: boolean) {
        siteToActivate.isLoading = true;
        this._sitesDataService.updateSiteAutoRenew(siteToActivate.appId!, { id: siteToActivate.id, isAutoRenewEnabled: newAutoRenewStatus } as Site, newAutoRenewStatus).subscribe({
            next: (site: Site) => {
                this._snackBar.open(`The site '${siteToActivate.siteName}' is now '${site.isAutoRenewEnabled ? 'set to auto-renew' : 'not set to auto-renew'}'`, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                this.updateSiteRenewalInformation(siteToActivate.appId, site);
                siteToActivate.isLoading = false;
            },
            error: (error) => {
                switch (error.error) {
                    case ErrorMessages.pastSiteMaxLimit:
                        this._snackBar.open(`Action disabled. You are over your active site limit.`, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                        break;
                    case ErrorMessages.invalidChargeNumber:
                        this._snackBar.open(ErrorMessages.invalidChargeNumber, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                        break;
                    default:
                        this._snackBar.open('There was an error updating the record', SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
                siteToActivate.isLoading = false;
            },
            complete: () => { },
        });
    }

    getActiveSitesCountByAppId(siteToActivate: SiteRenewal) {
        this._sitesDataService.getSitesCountByAppId(siteToActivate.appId!).pipe(map((activeSitesCount: number) => {
            const enterpriseApp = this.model.appsSiteManagement.appsManagement.find(app => app.appId === siteToActivate.appId);
            enterpriseApp!.currentActiveSitesCount = activeSitesCount;
        })).subscribe();
    }

    updateSiteRenewalInformation(appId: string, site: Site) {
        this.model.appsSiteManagement.siteRenewals = this.model.appsSiteManagement.siteRenewals.map(siteRenewal => {
            if (siteRenewal.id === site.id && siteRenewal.appId === appId) {
                siteRenewal.active = site.active;
                siteRenewal.isAutoRenewEnabled = site.isAutoRenewEnabled;
                if (this.isSiteAppPerSite(siteRenewal)) {
                    if (site.subscriptionStartDate) {
                        siteRenewal.subscriptionStartDate = this._dateConversionService.safeParseDate(site.subscriptionStartDate);
                    }

                    if (site.renewDate) {
                        siteRenewal.subscriptionEndDate = this._dateConversionService.safeParseDate(site.renewDate);
                    }
                }
            }
            return siteRenewal;
        });
    }

    isSiteAppPerSite(siteRenewal: SiteRenewal): boolean {
        return this.model.appsSiteManagement?.appsManagement.find(app => app.appId === siteRenewal.appId)?.activeLicenseTier.id === LicenseTiers.PerSite;
    }

    isRenewalDateYearFromNow(site: Site): boolean {
        const today = new Date();
        const siteRenewDate = new Date(site.renewDate);
        const newExpirationDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
        const siteRenewDateNormalized = new Date(siteRenewDate.getFullYear(), siteRenewDate.getMonth(), siteRenewDate.getDate());

        return siteRenewDateNormalized === newExpirationDate;
    }
}
